// @ts-check
/**
 * The single project to look at first, and why. Null whenever the report is stable, so the panel shows
 * nothing rather than inventing a concern.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { LuArrowRight } from 'react-icons/lu';

import { TestableButton, TestableText } from '@components/general/TestableComponents';
import { projectTitle } from '@datahooks/ProjectsListContext';
import { renderReportMarkdown } from './AIReportMarkdown.jsx';

/** @typedef {import('@datahooks/types/organisationAiReport.types.js').AiReportRecommendedFocus} AiReportRecommendedFocus */
/** @typedef {import('@datahooks/ProjectsListContext').BasicProject} BasicProject */

/**
 * Renders the recommended focus for a single project within an AI report.
 * 
 * @param {{ focus: AiReportRecommendedFocus | null, projects?: BasicProject[] }} props
 * @returns {JSX.Element|null}
 */
const AIReportFocus = ({ focus, projects = [] }) => {
  const navigate = useNavigate();

  if (!focus) {
    return null;
  }

  const { projectId, projectName, reason, tags = [] } = focus;
  const displayName = projectTitle(projects, projectId, projectName);

  return (
    <div
      className="rounded-tremor-default border-l-4 border-red-500 bg-red-50 p-4 dark:bg-red-950/40"
      data-testid="ai-report-focus"
    >
      <TestableText
        variant="small"
        className="text-xs font-semibold uppercase tracking-wider text-red-500"
        dataTestId="ai-report-focus-label"
      >
        Recommended focus
      </TestableText>
      <TestableText
        variant="small"
        className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100"
        dataTestId="ai-report-focus-project"
      >
        {displayName}
      </TestableText>

      <div
        className="mt-2 space-y-2 text-sm leading-6 text-gray-700 dark:text-gray-300"
        data-testid="ai-report-focus-reason"
      >
        {renderReportMarkdown(reason, 'ai-report-focus-reason')}
      </div>

      {tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-report-focus-tags">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              data-testid="ai-report-focus-tag"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/*
        Navigates rather than links: leaving the page closes the panel, which the provider already does
        on a route change, and a button keeps the call to action distinct from the inline project links.
      */}
      <TestableButton
        dataTestId="ai-report-focus-action"
        color="blue"
        size="sm"
        onClick={() => navigate(`/projects/${projectId}`)}
        className="mt-4 flex items-center gap-2 normal-case"
      >
        Review {displayName}
        <LuArrowRight aria-hidden="true" className="h-4 w-4" />
      </TestableButton>
    </div>
  );
};

AIReportFocus.propTypes = {
  focus: PropTypes.shape({
    projectId: PropTypes.number.isRequired,
    projectName: PropTypes.string.isRequired,
    reason: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    title: PropTypes.string,
  })),
};

export default AIReportFocus;
